import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { useAudio } from '@/contexts/AudioContext';
import {
  BookOpen, Users, CalendarCheck, Calendar, Store, Volume2, VolumeX,
  Shield, LayoutDashboard, Crown,
} from 'lucide-react';
import PractitionerOnboardingBanner from '@/components/PractitionerOnboardingBanner';
import { CompanionPanelBanner } from '@/components/CompanionPanelNotice';

const BG_IMAGE = 'https://images.unsplash.com/photo-1679615845580-8691c78fd7d3?q=80&w=2000';

const BLOCKS = [
  { key: 'coaching',    label: 'Coaching Program', path: '/dashboard/modules',     icon: BookOpen,      area: 'b1' },
  { key: 'workshops',   label: 'Workshops',        path: '/dashboard/workshops',   icon: Users,         area: 'b2' },
  { key: 'marketplace', label: 'Marketplace',      path: '/dashboard/marketplace', icon: Store,         area: 'b3' },
  { key: 'booking',     label: 'Session Booking',  path: '/dashboard/bookings',    icon: CalendarCheck, area: 'b4' },
  { key: 'events',      label: 'Events',           path: '/dashboard/calendar',    icon: Calendar,      area: 'b5' },
];

const PortalLanding = ({ user }) => {
  const navigate = useNavigate();
  const { stopMusic } = useAudio();
  const videoRef = useRef(null);
  const previewRefs = useRef({});
  const [videoUrl, setVideoUrl] = useState(null);
  const [blockPreviews, setBlockPreviews] = useState({});
  const [showUnmute, setShowUnmute] = useState(false);
  const [swirling, setSwirling] = useState(null);
  const [ignite, setIgnite] = useState(false);
  const [leaving, setLeaving] = useState(null);
  const [isApprovedVendor, setIsApprovedVendor] = useState(false);
  const [hasPlatinumAccess, setHasPlatinumAccess] = useState(false);

  // Never let background music play over the welcome video
  useEffect(() => {
    stopMusic();
  }, [stopMusic]);

  const loadVideo = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/portal/welcome-video`);
      setVideoUrl(res.data.video_url || null);
      if (res.data.video_type === 'first_login') {
        axios.post(`${API}/portal/welcome-video/seen`).catch(() => {});
      }
    } catch (e) {
      setVideoUrl(null);
    }
  }, []);

  useEffect(() => { loadVideo(); }, [loadVideo]);

  useEffect(() => {
    axios.get(`${API}/portal/block-previews`)
      .then((r) => setBlockPreviews(r.data || {}))
      .catch(() => {});
  }, []);

  // Gated shortcuts — mirrors the sidebar's rules so the two can never drift.
  useEffect(() => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    axios.get(`${API}/vendor/me`, { headers })
      .then((r) => setIsApprovedVendor(r.data?.status === 'approved'))
      .catch(() => setIsApprovedVendor(false));
    axios.get(`${API}/platinum-plus/access`, { headers })
      .then((r) => setHasPlatinumAccess(!!r.data?.access))
      .catch(() => setHasPlatinumAccess(false));
  }, []);

  const isAdmin    = user?.role === 'admin';
  const isFreeComp = user?.membership_type === 'free_comp';
  const shortcuts = [];
  if (isAdmin) {
    shortcuts.push({ key: 'admin', label: 'Admin Panel', path: '/dashboard/admin', icon: Shield });
  }
  if (isAdmin || isFreeComp) {
    shortcuts.push({ key: 'hub', label: 'Practitioner Hub', path: '/dashboard/practitioner-hub', icon: LayoutDashboard });
  } else if (isApprovedVendor) {
    shortcuts.push({ key: 'hub', label: 'Marketplace Hub', path: '/dashboard/practitioner-hub', icon: LayoutDashboard });
  }
  if (hasPlatinumAccess) {
    shortcuts.push({ key: 'platinum', label: 'Platinum+', path: '/dashboard/platinum-plus', icon: Crown });
  }

  const handleBlockEnter = (key) => {
    const v = previewRefs.current[key];
    if (v) { v.currentTime = 0; v.play().catch(() => {}); }
  };
  const handleBlockLeave = (key) => {
    const v = previewRefs.current[key];
    if (v) { v.pause(); v.currentTime = 0; }
  };

  const onWelcomePlay = () => {
    setIgnite(true);
    setTimeout(() => setIgnite(false), 1300);
  };

  // Attempt autoplay WITH sound (carried by the login click gesture);
  // silently fall back to muted + a "tap for sound" control if blocked.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !videoUrl) return;
    v.muted = false;
    const p = v.play();
    if (p && p.catch) {
      p.catch(() => {
        v.muted = true;
        setShowUnmute(true);
        v.play().catch(() => {});
      });
    }
  }, [videoUrl]);

  const unmute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    setShowUnmute(false);
    v.play().catch(() => {});
  };

  const openPortal = (path) => {
    if (swirling || leaving) return;
    setSwirling(path);
    setTimeout(() => navigate(path), 750);
  };

  // Shortcuts step sideways rather than being pulled through — a short fade, no swirl.
  const openShortcut = (path) => {
    if (swirling || leaving) return;
    setLeaving(path);
    setTimeout(() => navigate(path), 250);
  };

  const firstName = (user?.full_name || user?.username || '').split(' ')[0];

  return (
    <div className="portal-root">
      <style>{`
        .portal-root {
          position: fixed; inset: 0; z-index: 55;
          overflow-y: auto;
          background: #0a0e1a;
        }
        .portal-bg {
          position: fixed; inset: 0; z-index: 0;
          background-image: url(${BG_IMAGE});
          background-size: cover; background-position: center;
        }
        .portal-overlay {
          position: fixed; inset: 0; z-index: 1;
          background: linear-gradient(to bottom, rgba(10,14,26,0.72) 0%, rgba(26,35,50,0.78) 55%, rgba(15,16,40,0.88) 100%);
        }
        .portal-stars {
          position: fixed; inset: -50%; z-index: 1; pointer-events: none; opacity: .75;
          background-image:
            radial-gradient(2px 2px at 20% 30%, rgba(255,255,255,.85), transparent),
            radial-gradient(1px 1px at 60% 70%, rgba(159,236,236,.9), transparent),
            radial-gradient(1.5px 1.5px at 80% 20%, rgba(255,255,255,.7), transparent),
            radial-gradient(1px 1px at 40% 80%, rgba(200,180,255,.85), transparent),
            radial-gradient(1.5px 1.5px at 90% 60%, rgba(255,255,255,.6), transparent),
            radial-gradient(1px 1px at 10% 60%, rgba(159,236,236,.7), transparent),
            radial-gradient(2px 2px at 33% 15%, rgba(255,255,255,.7), transparent),
            radial-gradient(1px 1px at 70% 45%, rgba(255,255,255,.6), transparent);
          background-size: 50% 50%;
          animation: starDrift 80s linear infinite, starTwinkle 6s ease-in-out infinite;
        }
        .portal-stars.s2 {
          opacity: .4; filter: blur(.5px);
          animation: starDrift 120s linear infinite reverse, starTwinkle 8s ease-in-out infinite;
        }
        @keyframes starDrift {
          from { transform: translate3d(0,0,0) rotate(0deg); }
          to   { transform: translate3d(4%, 5%, 0) rotate(8deg); }
        }
        @keyframes starTwinkle { 0%,100% { opacity: .35; } 50% { opacity: .8; } }
        .portal-content {
          position: relative; z-index: 2;
          min-height: 100%;
          display: flex; flex-direction: column; justify-content: center;
          padding: 2.25rem 1.5rem;
        }
        .portal-greeting { text-align: center; margin-bottom: 2rem; }
        .portal-greeting h1 {
          font-family: 'Cormorant Garamond', serif; font-weight: 700;
          color: #fff; font-size: clamp(2rem, 4vw, 3.25rem); line-height: 1.1;
        }
        .portal-greeting p { color: #9fe9e9; margin-top: .5rem; font-size: 1.05rem; letter-spacing: .02em; }

        .portal-grid {
          max-width: 1180px; margin: 0 auto;
          display: grid; gap: 1.5rem;
          grid-template-columns: 1fr 1.5fr 1fr;
          grid-template-areas: "b1 b2 b3" "b4 video b5";
          align-items: stretch;
        }
        .portal-video-wrap { grid-area: video; position: relative; z-index: 2; }
        .portal-ring {
          position: absolute; inset: -22px; border-radius: 30px; z-index: 1; pointer-events: none;
          background: conic-gradient(from 0deg,
            rgba(102,204,204,0) 0deg,
            rgba(102,204,204,0.55) 60deg,
            rgba(150,90,220,0.55) 140deg,
            rgba(102,204,204,0) 210deg,
            rgba(120,220,220,0.5) 290deg,
            rgba(150,90,220,0) 340deg,
            rgba(102,204,204,0) 360deg);
          filter: blur(16px);
          animation: portalRingSpin 14s linear infinite;
          transition: filter .45s ease;
        }
        .portal-video-wrap.igniting .portal-ring { filter: blur(16px) brightness(2.1); }
        .portal-flare {
          position: absolute; inset: -22px; border-radius: 30px; z-index: 1; pointer-events: none;
          opacity: 0;
          background: radial-gradient(circle, rgba(180,245,245,0.95) 0%, rgba(102,204,204,0.45) 42%, transparent 70%);
        }
        .portal-video-wrap.igniting .portal-flare { animation: portalIgnite 1.3s ease-out; }
        @keyframes portalIgnite {
          0%   { opacity: 0;   transform: scale(0.8); }
          22%  { opacity: 1;   transform: scale(1.12); }
          100% { opacity: 0;   transform: scale(1.55); }
        }
        .portal-ring.two {
          inset: -38px; border-radius: 38px; opacity: .55;
          animation-duration: 22s; animation-direction: reverse; filter: blur(24px);
        }
        .portal-halo {
          position: absolute; inset: -8px; border-radius: 24px; z-index: 1; pointer-events: none;
          animation: portalHaloPulse 4.5s ease-in-out infinite;
        }
        @keyframes portalRingSpin { to { transform: rotate(360deg); } }
        @keyframes portalHaloPulse {
          0%,100% { box-shadow: 0 0 24px 2px rgba(102,204,204,0.25); }
          50%     { box-shadow: 0 0 46px 6px rgba(102,204,204,0.5); }
        }
        .portal-particle {
          position: absolute; top: 50%; left: 50%; width: 5px; height: 5px; margin: -2.5px;
          border-radius: 50%; z-index: 1; pointer-events: none;
          background: rgba(159,236,236,0.9);
          box-shadow: 0 0 10px 2px rgba(102,204,204,0.7);
        }
        @keyframes portalOrbit {
          from { transform: rotate(0deg) translateX(var(--r)) rotate(0deg); }
          to   { transform: rotate(360deg) translateX(var(--r)) rotate(-360deg); }
        }
        .portal-video-frame {
          position: relative; z-index: 2; border-radius: 20px; overflow: hidden;
          border: 1.5px solid rgba(102,204,204,0.4);
          box-shadow: 0 0 40px rgba(102,204,204,0.22), inset 0 0 30px rgba(45,30,90,0.4);
          background: #0b0f1e; aspect-ratio: 16/10; width: 100%;
          display: flex; align-items: center; justify-content: center;
        }
        .portal-video-frame video { width: 100%; height: 100%; object-fit: cover; }
        .portal-video-placeholder { color: #6b7a99; text-align: center; padding: 2rem; font-size: .95rem; }
        .portal-unmute {
          position: absolute; bottom: 12px; right: 12px; z-index: 3;
          display: inline-flex; align-items: center; gap: .4rem;
          background: rgba(10,14,26,0.75); color: #bff6f6;
          border: 1px solid rgba(102,204,204,0.6); border-radius: 999px;
          padding: .4rem .85rem; font-size: .85rem; cursor: pointer;
          backdrop-filter: blur(6px); transition: all .2s ease;
        }
        .portal-unmute:hover { background: rgba(102,204,204,0.2); border-color: rgba(102,204,204,0.9); }

        .portal-block {
          position: relative; cursor: pointer; border-radius: 18px;
          padding: 1.75rem 1rem; min-height: 150px;
          display: flex; flex-direction: column; align-items: center; justify-content: center; gap: .85rem;
          background: rgba(20,26,48,0.55);
          border: 1px solid rgba(102,204,204,0.22);
          box-shadow: 0 8px 26px rgba(0,0,0,0.35);
          backdrop-filter: blur(10px);
          transition: transform .3s ease, box-shadow .3s ease, border-color .3s ease, background .3s ease;
        }
        .portal-block:hover {
          transform: translateY(-6px);
          border-color: rgba(102,204,204,0.7);
          background: rgba(28,36,66,0.7);
          box-shadow: 0 14px 38px rgba(102,204,204,0.28);
        }
        .portal-block .pb-icon {
          width: 58px; height: 58px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          background: radial-gradient(circle at 30% 30%, rgba(102,204,204,0.28), rgba(80,50,150,0.28));
          border: 1px solid rgba(102,204,204,0.45);
          box-shadow: 0 0 18px rgba(102,204,204,0.25);
          transition: box-shadow .3s ease;
        }
        .portal-block:hover .pb-icon { box-shadow: 0 0 28px rgba(102,204,204,0.55); }
        .portal-block .pb-icon, .portal-block .pb-label { position: relative; z-index: 2; }
        .portal-block .pb-label {
          font-family: 'Cormorant Garamond', serif; font-weight: 600;
          color: #f2f6ff; font-size: 1.25rem; text-align: center; line-height: 1.15;
          text-shadow: 0 1px 8px rgba(0,0,0,0.6);
        }
        .portal-block video.pb-preview {
          position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover;
          border-radius: 18px; opacity: 0; z-index: 0;
          transition: opacity .45s ease;
        }
        .portal-block.has-preview:hover video.pb-preview { opacity: .92; }
        .portal-block .pb-scrim {
          position: absolute; inset: 0; border-radius: 18px; z-index: 1; pointer-events: none;
          background: linear-gradient(to top, rgba(8,10,22,0.82), rgba(8,10,22,0.3));
          opacity: 0; transition: opacity .45s ease;
        }
        .portal-block.has-preview:hover .pb-scrim { opacity: 1; }

        @keyframes portalSwirl {
          0%   { transform: rotate(0deg) scale(1);      opacity: 1; }
          60%  { transform: rotate(320deg) scale(0.45); opacity: .85; box-shadow: 0 0 60px rgba(102,204,204,0.8); }
          100% { transform: rotate(540deg) scale(0);    opacity: 0; box-shadow: 0 0 90px rgba(102,204,204,1); }
        }
        .portal-block.swirling {
          animation: portalSwirl .75s cubic-bezier(.5,.05,.6,1) forwards;
          border-color: rgba(102,204,204,1);
          z-index: 5;
        }
        .portal-fading { animation: portalFade .75s ease forwards; }
        @keyframes portalFade { to { opacity: .25; filter: blur(2px); } }

        /* Gated shortcuts — same cyan language as the portals, a third the weight */
        .portal-shortcuts {
          max-width: 1180px; margin: 1.75rem auto 0;
          display: flex; align-items: center; justify-content: center;
          flex-wrap: wrap; gap: .75rem;
        }
        .portal-shortcuts-rule {
          max-width: 1180px; margin: 2rem auto 0; height: 1px;
          background: linear-gradient(to right,
            rgba(102,204,204,0) 0%, rgba(102,204,204,0.28) 50%, rgba(102,204,204,0) 100%);
        }
        .portal-chip {
          display: inline-flex; align-items: center; gap: .55rem;
          padding: .6rem 1.15rem; border-radius: 999px; cursor: pointer;
          font-family: 'Cormorant Garamond', serif; font-weight: 600;
          font-size: 1.02rem; letter-spacing: .01em; color: #dff6f6;
          background: rgba(20,26,48,0.55);
          border: 1px solid rgba(102,204,204,0.22);
          box-shadow: 0 6px 18px rgba(0,0,0,0.3);
          backdrop-filter: blur(10px);
          transition: transform .25s ease, border-color .25s ease,
                      background .25s ease, box-shadow .25s ease, color .25s ease;
        }
        .portal-chip:hover {
          transform: translateY(-3px);
          color: #ffffff;
          border-color: rgba(102,204,204,0.7);
          background: rgba(28,36,66,0.7);
          box-shadow: 0 10px 26px rgba(102,204,204,0.28);
        }
        .portal-chip:focus-visible {
          outline: none;
          border-color: rgba(102,204,204,0.9);
          box-shadow: 0 0 0 3px rgba(102,204,204,0.3);
        }
        .portal-chip svg { color: #9fe9e9; transition: color .25s ease; }
        .portal-chip:hover svg { color: #bff6f6; }
        .portal-chip.leaving {
          animation: chipLeave .25s ease forwards;
          border-color: rgba(102,204,204,0.9);
        }
        @keyframes chipLeave {
          to { transform: translateY(-6px) scale(.97); opacity: 0; }
        }

        @media (max-width: 900px) {
          .portal-grid {
            grid-template-columns: 1fr 1fr;
            grid-template-areas: "video video" "b1 b2" "b3 b4" "b5 b5";
          }
          .portal-video-frame { aspect-ratio: 16/9; }
          .portal-block { min-height: 120px; padding: 1.25rem .75rem; }
          .portal-shortcuts-rule { margin-top: 1.5rem; }
          .portal-shortcuts { margin-top: 1.25rem; gap: .6rem; }
          .portal-chip { padding: .5rem .9rem; font-size: .95rem; }
        }
      `}</style>

      <div className="portal-bg" />
      <div className="portal-overlay" />
      <div className="portal-stars" />
      <div className="portal-stars s2" />

      <div className="portal-content">
        {/* The portal covers the dashboard chrome, so the onboarding prompt has to
            live in here too — otherwise a new practitioner lands on the one page
            where they can see neither the banner nor the notification bell. */}
        <PractitionerOnboardingBanner placement="portal" />
        <CompanionPanelBanner placement="portal" />

        <div className="portal-greeting">
          <h1>{firstName ? `Welcome, ${firstName}` : 'Welcome'}</h1>
          <p>Choose your path — your portal awaits</p>
        </div>

        <div className={`portal-grid ${swirling ? 'portal-fading' : ''}`}>
          {BLOCKS.map((b) => {
            const Icon = b.icon;
            const preview = blockPreviews[b.key];
            return (
              <div
                key={b.key}
                style={{ gridArea: b.area }}
                className={`portal-block ${preview ? 'has-preview' : ''} ${swirling === b.path ? 'swirling' : ''}`}
                onClick={() => openPortal(b.path)}
                onMouseEnter={() => preview && handleBlockEnter(b.key)}
                onMouseLeave={() => preview && handleBlockLeave(b.key)}
                data-testid={`portal-block-${b.key}`}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') openPortal(b.path); }}
              >
                {preview && (
                  <>
                    <video
                      ref={(el) => { previewRefs.current[b.key] = el; }}
                      className="pb-preview"
                      src={`${process.env.REACT_APP_BACKEND_URL}${preview}`}
                      muted
                      loop
                      playsInline
                      preload="metadata"
                      data-testid={`portal-preview-${b.key}`}
                    />
                    <span className="pb-scrim" />
                  </>
                )}
                <span className="pb-icon"><Icon className="w-7 h-7 text-cyan-200" /></span>
                <span className="pb-label">{b.label}</span>
              </div>
            );
          })}

          <div className={`portal-video-wrap ${ignite ? 'igniting' : ''}`}>
            <div className="portal-ring" />
            <div className="portal-ring two" />
            <div className="portal-flare" />
            <div className="portal-halo" />
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
              <span
                key={i}
                className="portal-particle"
                style={{
                  '--r': `${168 + i * 20}px`,
                  animation: `portalOrbit ${11 + (i % 5) * 3}s linear infinite`,
                  animationDelay: `${-i * 1.6}s`,
                  opacity: 0.45 + (i % 3) * 0.2,
                }}
              />
            ))}
            <div className="portal-video-frame" data-testid="portal-video-frame">
              {videoUrl ? (
                <video
                  ref={videoRef}
                  src={`${process.env.REACT_APP_BACKEND_URL}${videoUrl}`}
                  playsInline
                  autoPlay
                  controls
                  onPlay={onWelcomePlay}
                  data-testid="portal-welcome-video"
                />
              ) : (
                <div className="portal-video-placeholder">
                  Your welcome video will appear here.
                </div>
              )}
              {videoUrl && showUnmute && (
                <button className="portal-unmute" onClick={unmute} data-testid="portal-unmute-btn">
                  <VolumeX className="w-4 h-4" /> Tap for sound
                </button>
              )}
              {videoUrl && !showUnmute && (
                <button
                  className="portal-unmute"
                  onClick={() => { const v = videoRef.current; if (v) { v.muted = !v.muted; setShowUnmute(v.muted); } }}
                  data-testid="portal-mute-toggle"
                  title="Toggle sound"
                >
                  <Volume2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {shortcuts.length > 0 && (
          <div className={swirling ? 'portal-fading' : ''}>
            <div className="portal-shortcuts-rule" />
            <div className="portal-shortcuts" data-testid="portal-shortcuts">
              {shortcuts.map((s) => {
                const Icon = s.icon;
                return (
                  <button
                    key={s.key}
                    type="button"
                    className={`portal-chip ${leaving === s.path ? 'leaving' : ''}`}
                    onClick={() => openShortcut(s.path)}
                    data-testid={`portal-shortcut-${s.key}`}
                  >
                    <Icon className="w-4 h-4" />
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PortalLanding;
